import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import { OpcionesEditor } from './OpcionesEditor'
import type { Campo, CampoCreatePayload, TipoCampo } from '../types'
import { TIPO_CAMPO_LABELS } from '../types'

const campoSchema = z.object({
  tipo: z.string().min(1, 'Requerido'),
  etiqueta: z.string().min(1, 'La etiqueta es requerida'),
  descripcion: z.string().optional(),
  requerido: z.boolean(),
  placeholder: z.string().optional(),
  min_valor: z.coerce.number().optional().nullable(),
  max_valor: z.coerce.number().optional().nullable(),
  max_caracteres: z.coerce.number().optional().nullable(),
})

type CampoFormData = z.infer<typeof campoSchema>

interface CampoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CampoCreatePayload) => void
  isSubmitting: boolean
  campo?: Campo | null
  mode: 'create' | 'edit'
}

export function CampoDialog({ open, onOpenChange, onSubmit, isSubmitting, campo, mode }: CampoDialogProps) {
  const [opciones, setOpciones] = useState<string[]>([])
  const [escalaEtiquetas, setEscalaEtiquetas] = useState<Record<string, string>>({})

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<CampoFormData>({
    resolver: zodResolver(campoSchema),
    defaultValues: {
      tipo: 'texto',
      etiqueta: '',
      descripcion: '',
      requerido: false,
      placeholder: '',
      min_valor: null,
      max_valor: null,
      max_caracteres: null,
    },
  })

  const tipo = watch('tipo') as TipoCampo
  const minValor = watch('min_valor')
  const maxValor = watch('max_valor')

  const tieneOpciones = ['select', 'radio', 'checkbox'].includes(tipo)
  const tieneRango = ['numero', 'escala'].includes(tipo)
  const tieneMaxCaracteres = tipo === 'textarea'
  const esEscala = tipo === 'escala'

  useEffect(() => {
    if (open && mode === 'edit' && campo) {
      reset({
        tipo: campo.tipo,
        etiqueta: campo.etiqueta,
        descripcion: campo.descripcion || '',
        requerido: campo.requerido,
        placeholder: campo.placeholder || '',
        min_valor: campo.min_valor,
        max_valor: campo.max_valor,
        max_caracteres: campo.max_caracteres,
      })
      setOpciones(campo.opciones.map(o => o.valor))
      setEscalaEtiquetas(campo.escala_etiquetas || {})
    } else if (open && mode === 'create') {
      reset({
        tipo: 'texto',
        etiqueta: '',
        descripcion: '',
        requerido: false,
        placeholder: '',
        min_valor: null,
        max_valor: null,
        max_caracteres: null,
      })
      setOpciones([])
      setEscalaEtiquetas({})
    }
  }, [open, mode, campo, reset])

  // Generar campos de etiquetas de escala cuando cambia el rango
  const escalaValores: number[] = []
  if (esEscala && minValor != null && maxValor != null && maxValor > minValor) {
    for (let i = Number(minValor); i <= Number(maxValor); i++) {
      escalaValores.push(i)
    }
  }

  const handleFormSubmit = (data: CampoFormData) => {
    const payload: CampoCreatePayload = {
      tipo: data.tipo as TipoCampo,
      etiqueta: data.etiqueta,
      descripcion: data.descripcion || '',
      requerido: data.requerido,
      placeholder: data.placeholder || '',
      min_valor: tieneRango ? data.min_valor : null,
      max_valor: tieneRango ? data.max_valor : null,
      max_caracteres: tieneMaxCaracteres ? data.max_caracteres : null,
      escala_etiquetas: esEscala && Object.keys(escalaEtiquetas).length > 0 ? escalaEtiquetas : null,
      opciones: tieneOpciones ? opciones.filter(o => o.trim()) : [],
    }
    onSubmit(payload)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Nuevo Campo' : 'Editar Campo'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo de campo *</Label>
            <Select value={tipo} onValueChange={(v) => setValue('tipo', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(TIPO_CAMPO_LABELS) as [TipoCampo, string][]).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="etiqueta">Etiqueta *</Label>
            <Input id="etiqueta" {...register('etiqueta')} placeholder="Pregunta o título del campo" />
            {errors.etiqueta && <p className="text-sm text-destructive">{errors.etiqueta.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción / Ayuda</Label>
            <Textarea id="descripcion" {...register('descripcion')} rows={2} placeholder="Texto de ayuda (opcional)" />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="requerido">Campo obligatorio</Label>
            <Switch id="requerido" checked={watch('requerido')} onCheckedChange={(v) => setValue('requerido', v)} className="data-[state=checked]:bg-teal-500" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="placeholder">Placeholder</Label>
            <Input id="placeholder" {...register('placeholder')} placeholder="Texto de ejemplo" />
          </div>

          {/* Rango para número/escala */}
          {tieneRango && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_valor">Valor mínimo</Label>
                <Input id="min_valor" type="number" {...register('min_valor')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_valor">Valor máximo</Label>
                <Input id="max_valor" type="number" {...register('max_valor')} />
              </div>
            </div>
          )}

          {/* Etiquetas de escala */}
          {esEscala && escalaValores.length > 0 && (
            <div className="space-y-2">
              <Label>Etiquetas de escala</Label>
              <div className="space-y-2">
                {escalaValores.map((valor) => (
                  <div key={valor} className="flex items-center gap-2">
                    <span className="w-8 text-center font-medium text-sm">{valor}</span>
                    <Input
                      value={escalaEtiquetas[String(valor)] || ''}
                      onChange={(e) => {
                        const updated = { ...escalaEtiquetas }
                        if (e.target.value) {
                          updated[String(valor)] = e.target.value
                        } else {
                          delete updated[String(valor)]
                        }
                        setEscalaEtiquetas(updated)
                      }}
                      placeholder={`Etiqueta para valor ${valor}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Max caracteres para textarea */}
          {tieneMaxCaracteres && (
            <div className="space-y-2">
              <Label htmlFor="max_caracteres">Máximo de caracteres</Label>
              <Input id="max_caracteres" type="number" {...register('max_caracteres')} />
            </div>
          )}

          {/* Opciones para select/radio/checkbox */}
          {tieneOpciones && (
            <div className="space-y-2">
              <Label>Opciones</Label>
              <OpcionesEditor opciones={opciones} onChange={setOpciones} />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={isSubmitting} className="bg-teal-800 hover:bg-teal-900 text-white">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === 'create' ? 'Crear campo' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
